import { useRef, useState } from 'react'
import { db } from '../../firebase/config'
import recordStatusChange from '../../helpers/statusHistory/recordStatusChange'
import statusChangePatch from '../../helpers/personas/statusChangePatch'

// Eén statuswissel van een deelnemer, met het venster voor reden en datum bij
// uitstroom en inactiviteit. Nodig omdat Personas.jsx en PersonaBuilder.jsx
// dit elk zelf deden, met venster-state die na een wissel bleef staan en bij
// de VOLGENDE wissel (ook van een andere deelnemer) werd weggeschreven.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
// AI-CONTEXT: See docs/pitfalls.md#state-dialog-draft-outlives-its-target

const NEEDS_DIALOG = ['outflow', 'inactive']

/**
 * @typedef {object} StatusChangeTarget
 * @property {string} docid - Firestore-docid van de deelnemer
 * @property {string} personaId - `Personas.ID`, voor de statushistorie
 * @property {string} fromStatus - De status van vóór de wissel
 */

/**
 * @param {object} [options]
 * @param {string} [options.changedBy] - Users.ID van wie de wissel doet
 * @param {() => void} [options.onSaved] - Na een geslaagde wissel (het opgeslagen-vinkje)
 */
const usePersonaStatusChange = ({ changedBy, onSaved } = {}) => {
    const [target, setTarget] = useState(/** @type {StatusChangeTarget | null} */ (null))
    const [status, setStatus] = useState('')
    const [reason, setReason] = useState('')
    const [date, setDate] = useState('')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    // Een ref naast de state: twee klikken in dezelfde render zien allebei
    // `saving === false`, de ref ziet de eerste wél.
    const savingRef = useRef(false)

    const reset = () => {
        setTarget(null)
        setStatus('')
        setReason('')
        setDate('')
        setError('')
    }

    /**
     * @param {StatusChangeTarget} next
     * @param {string} toStatus
     * @param {{reason?: string, date?: string}} [draft]
     */
    /**
     * @returns {Promise<string>} Een foutmelding, of een lege tekst als het gelukt is
     */
    const write = async (next, toStatus, draft) => {
        if (savingRef.current) return ''
        savingRef.current = true
        setSaving(true)
        setError('')
        try {
            await db.collection('Personas').doc(next.docid).update(statusChangePatch(toStatus, draft))
        } catch (err) {
            // Zichtbaar maken, niet slikken: de status staat dan NIET om, en het
            // scherm moet dat zeggen in plaats van stil te blijven staan.
            console.error('statuswissel niet opgeslagen:', err)
            const melding = `De status is niet opgeslagen: ${err?.message || err}`
            setError(melding)
            savingRef.current = false
            setSaving(false)
            return melding
        }
        // Faalt zelf nooit hard (zie recordStatusChange); de wissel staat er al.
        await recordStatusChange({
            entityType: 'persona',
            entityID: next.personaId,
            fromStatus: next.fromStatus,
            toStatus,
            changedBy,
        })
        savingRef.current = false
        setSaving(false)
        if (onSaved) onSaved()
        return ''
    }

    /**
     * Uitstroom en inactiviteit openen het venster (leeg); elke andere status
     * wordt meteen geschreven.
     * @param {StatusChangeTarget} next
     * @param {string} toStatus
     * @returns {Promise<string>|undefined} Bij een directe wissel: de foutmelding, of een lege tekst
     */
    const request = (next, toStatus) => {
        if (NEEDS_DIALOG.includes(toStatus)) {
            setTarget(next)
            setStatus(toStatus)
            setReason('')
            setDate('')
            return undefined
        }
        return write(next, toStatus)
    }

    const confirm = async () => {
        if (!target || savingRef.current) return
        const melding = await write(target, status, { reason, date })
        // Bij een fout blijft het venster open, met wat er ingevuld was.
        if (!melding) reset()
    }

    return {
        open: Boolean(target),
        saving,
        error,
        status,
        reason,
        date,
        setReason,
        setDate,
        request,
        confirm,
        cancel: reset,
    }
}

export default usePersonaStatusChange
